import React, { useState } from 'react';
import { Printer, Settings2, Shuffle, Image as ImageIcon, Type, LayoutTemplate } from 'lucide-react';
import { Button } from '../ui';

interface BingoCard {
  id: string;
  grid: (number | string)[][]; // 5x5 grid
}

// Generate a random subset of numbers for a specific BINGO column
const generateColumn = (min: number, max: number, count: number): number[] => {
  const pool = Array.from({ length: max - min + 1 }, (_, i) => i + min);
  const result: number[] = [];
  for (let i = 0; i < count; i++) {
    const r = Math.floor(Math.random() * pool.length);
    result.push(pool.splice(r, 1)[0]);
  }
  return result;
};

const generateSingleCard = (centerText: string): BingoCard => {
  const b = generateColumn(1, 15, 5);
  const i = generateColumn(16, 30, 5);
  const n = generateColumn(31, 45, 5);
  const g = generateColumn(46, 60, 5);
  const o = generateColumn(61, 75, 5);

  // Set the FREE space
  const nWithFree: (number | string)[] = [...n];
  nWithFree[2] = centerText || 'FREE';

  // Construct grid row by row
  const grid: (number | string)[][] = [];
  for (let row = 0; row < 5; row++) {
    grid.push([b[row], i[row], nWithFree[row], g[row], o[row]]);
  }

  return {
    id: Math.random().toString(36).substring(2, 9),
    grid
  };
};

export function BingoCardGenerator() {
  const [quantity, setQuantity] = useState<number>(4);
  const [cards, setCards] = useState<BingoCard[]>([]);

  // Customization State
  const [headerText, setHeaderText] = useState<string>('');
  const [footerText, setFooterText] = useState<string>('');
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [centerSpaceText, setCenterSpaceText] = useState<string>('FREE');

  const handleGenerate = () => {
    const newCards = Array.from({ length: quantity }, () => generateSingleCard(centerSpaceText));
    setCards(newCards);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="w-full max-w-6xl mx-auto space-y-8 p-4 print:p-8">
      {/* Configuration UI - Hidden during print */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 p-6 md:p-8 print:hidden">
        <div className="flex flex-col md:flex-row gap-6 items-center justify-between mb-8">
          
          <div className="flex-1 space-y-2 w-full">
            <label className="text-sm font-semibold text-slate-500 uppercase flex items-center">
              <Settings2 className="w-4 h-4 mr-2" />
              Number of Cards to Generate
            </label>
            <div className="flex items-center gap-4">
              <input 
                type="range" 
                min="1" 
                max="50" 
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
                className="flex-1 accent-purple-600"
              />
              <span className="text-2xl font-black text-slate-900 dark:text-white w-12 text-center">
                {quantity}
              </span>
            </div>
            <p className="text-xs text-slate-500">Prints 2 cards per physical page.</p>
          </div>

          <div className="flex gap-4 w-full md:w-auto">
            <Button 
              onClick={handleGenerate}
              className="flex-1 md:flex-none h-14 px-8 text-lg font-bold bg-purple-600 hover:bg-purple-700 text-white shadow-lg"
            >
              <Shuffle className="w-5 h-5 mr-2" />
              Generate Cards
            </Button>
            <Button 
              onClick={handlePrint}
              disabled={cards.length === 0}
              variant="secondary"
              className="flex-1 md:flex-none h-14 px-8 text-lg font-bold border-2 border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <Printer className="w-5 h-5 mr-2" />
              Print
            </Button>
          </div>
        </div>

        <hr className="border-slate-200 dark:border-slate-800 mb-6" />

        {/* Customization Options */}
        <div className="space-y-4">
          <label className="text-sm font-semibold text-slate-500 uppercase flex items-center mb-4">
            <LayoutTemplate className="w-4 h-4 mr-2" />
            Card Customization
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 flex items-center">
                <Type className="w-3 h-3 mr-1" /> Header Text
              </label>
              <input 
                type="text" 
                value={headerText} 
                onChange={(e) => setHeaderText(e.target.value)} 
                placeholder="e.g. Annual Gala 2026"
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 flex items-center">
                <Type className="w-3 h-3 mr-1" /> Footer Text
              </label>
              <input 
                type="text" 
                value={footerText} 
                onChange={(e) => setFooterText(e.target.value)} 
                placeholder="e.g. Sponsored by ABC Corp"
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 flex items-center">
                <ImageIcon className="w-3 h-3 mr-1" /> Logo / Watermark URL
              </label>
              <input 
                type="url" 
                value={logoUrl} 
                onChange={(e) => setLogoUrl(e.target.value)} 
                placeholder="https://example.com/logo.png"
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 flex items-center">
                <LayoutTemplate className="w-3 h-3 mr-1" /> Center Space Text
              </label>
              <input 
                type="text" 
                value={centerSpaceText} 
                onChange={(e) => setCenterSpaceText(e.target.value)} 
                placeholder="FREE"
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Cards Preview / Print Area */}
      {cards.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 print:gap-12">
          {cards.map((card, index) => (
            <div 
              key={card.id} 
              className="relative bg-white border-2 border-slate-200 print:border-black rounded-xl p-6 shadow-sm print:shadow-none break-inside-avoid print:page-break-inside-avoid overflow-hidden flex flex-col"
              style={{
                pageBreakInside: 'avoid',
                // Force a page break every 2 cards in print mode, but not after the very last card
                pageBreakAfter: ((index + 1) % 2 === 0 && index !== cards.length - 1) ? 'always' : 'auto'
              }}
            >
              {/* Background Watermark */}
              {logoUrl && (
                <div className="absolute inset-0 z-0 flex items-center justify-center opacity-[0.08] print:opacity-10 pointer-events-none p-8">
                  <img src={logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" />
                </div>
              )}

              <div className="relative z-10 flex flex-col flex-1">
                <div className="text-center mb-6">
                  {headerText && (
                    <div className="text-xl print:text-2xl font-bold text-slate-800 print:text-black mb-3 border-b-2 border-slate-100 print:border-black/20 pb-2">
                      {headerText}
                    </div>
                  )}
                  <div className="flex items-center justify-center gap-4">
                    <h2 className="text-4xl font-black tracking-[0.5em] text-slate-900 print:text-black">BINGO</h2>
                  </div>
                  <div className="text-xs text-slate-400 print:text-black/50 mt-2 uppercase tracking-wider">
                    Card ID: {card.id.toUpperCase()}
                  </div>
                </div>
                
                <div className="grid grid-cols-5 gap-2 print:gap-1 flex-1">
                  {/* Headers */}
                  {['B', 'I', 'N', 'G', 'O'].map((letter) => (
                    <div key={letter} className="text-center font-black text-xl text-purple-700 print:text-black bg-purple-100 print:bg-gray-200 py-2 rounded-t-lg">
                      {letter}
                    </div>
                  ))}
                  
                  {/* Grid Cells */}
                  {card.grid.flatMap((row, rIdx) => 
                    row.map((cell, cIdx) => {
                      const isCenter = rIdx === 2 && cIdx === 2;
                      return (
                        <div 
                          key={`${rIdx}-${cIdx}`}
                          className="aspect-square flex flex-col items-center justify-center text-2xl print:text-xl font-black text-slate-900 print:text-black border-2 border-slate-200 print:border-black rounded-lg bg-slate-50/90 print:bg-white backdrop-blur-sm"
                        >
                          {isCenter ? (
                            <>
                              <div className="text-sm print:text-xs text-center px-1 break-words w-full leading-tight">{cell}</div>
                              {(!cell || cell === 'FREE') && <div className="text-xs print:text-[10px] text-slate-400 print:text-black/60 mt-1">SPACE</div>}
                            </>
                          ) : (
                            cell
                          )}
                        </div>
                      );
                    })
                  )}
                </div>

                {footerText && (
                  <div className="mt-6 pt-4 text-center text-sm print:text-xs text-slate-500 print:text-black/70 font-medium border-t-2 border-slate-100 print:border-black/20">
                    {footerText}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
